import React from 'react';
import { FileText, Users2, Swords, CalendarRange, TrendingUp } from 'lucide-react';

const decisionMakers = [
  { role: 'CFO', name: 'Priya S.', signal: 'Public: cost-of-capital focus' },
  { role: 'VP Marketing', name: 'James H.', signal: 'Hired 90d ago · previously at ACME' },
  { role: 'Head of Ops', name: 'Rachel D.', signal: 'Owns vendor selection · budget signed' },
];

const renewals = [
  { vendor: 'Legacy CRM (Inc.)', value: '$1.8M', months: 5, color: 'from-red-500 to-red-400' },
  { vendor: 'Analytics Suite (Inc.)', value: '$740K', months: 9, color: 'from-amber-500 to-amber-400' },
  { vendor: 'Data Pipeline (Inc.)', value: '$420K', months: 3, color: 'from-red-500 to-red-400' },
];

const AudienceIntelligenceMockup = () => {
  return (
    <div className="relative">
      {/* Stacked pages behind */}
      <div className="hidden md:block absolute -top-3 left-6 right-6 h-full rounded-2xl bg-white shadow-mk opacity-60" />
      <div className="hidden md:block absolute -top-1.5 left-3 right-3 h-full rounded-2xl bg-white shadow-mk opacity-80" />

      {/* Main "page" */}
      <div className="relative rounded-2xl bg-white shadow-mk-lg ring-1 ring-slate-200 overflow-hidden">
        {/* Document masthead */}
        <div className="flex items-center justify-between px-6 sm:px-8 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-teal-600 to-teal-400 text-white flex items-center justify-center shadow-[0_4px_11px_0_rgba(20,184,166,0.35)]">
              <FileText size={16} strokeWidth={2.4} />
            </div>
            <div>
              <p className="text-body-muted text-[10px] font-bold uppercase tracking-[0.16em]">Publisher Brand · Corporate Briefing</p>
              <p className="text-navy text-[13px] font-black tracking-tight">Target · Northwind Cloud</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-body-muted text-[10px] font-bold uppercase tracking-[0.16em]">Report No.</p>
            <p className="text-navy text-[13px] font-black">AI-04211 · v1</p>
          </div>
        </div>

        {/* Cover title */}
        <div className="px-6 sm:px-8 pt-8 pb-6 border-b border-slate-100">
          <p className="text-teal-700 text-[11px] font-bold uppercase tracking-[0.18em] mb-2">Executive summary · 12-week path to close</p>
          <h4 className="text-navy font-black text-[26px] sm:text-[30px] leading-[1.15] tracking-tight max-w-[520px]">
            $2.96M of contract value up for grabs. Three viable entry points. One aligned buyer.
          </h4>
          <div className="mt-5 flex flex-wrap gap-2">
            <span className="text-teal-700 bg-teal-50 text-[10.5px] font-bold uppercase tracking-[0.14em] rounded-full px-2.5 py-1">13 pages</span>
            <span className="text-navy bg-slate-100 text-[10.5px] font-bold uppercase tracking-[0.14em] rounded-full px-2.5 py-1">4 source classes</span>
            <span className="text-navy bg-slate-100 text-[10.5px] font-bold uppercase tracking-[0.14em] rounded-full px-2.5 py-1">Delivered · 42 hr</span>
          </div>
        </div>

        {/* Body: 2-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 lg:divide-x lg:divide-slate-100">
          {/* Decision architecture */}
          <div className="px-6 sm:px-8 py-6">
            <div className="flex items-center gap-2 text-teal-700 text-[10.5px] font-bold uppercase tracking-[0.14em]">
              <Users2 size={14} strokeWidth={2.4} />
              Section 1 · Decision architecture
            </div>
            <ul className="mt-3 space-y-2.5">
              {decisionMakers.map((d) => (
                <li key={d.role} className="flex items-start gap-3">
                  <div className="mt-0.5 h-8 w-8 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center shrink-0 text-[10.5px] font-black">
                    {d.role.split(' ').map((w) => w[0]).join('').slice(0, 2)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-navy text-[12.5px] font-bold leading-tight">{d.role} · <span className="text-body-muted font-semibold">{d.name}</span></p>
                    <p className="text-body-muted text-[11.5px] leading-snug mt-0.5">{d.signal}<sup className="text-teal-600 font-bold ml-0.5">³</sup></p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Contract stack & renewals */}
          <div className="px-6 sm:px-8 py-6">
            <div className="flex items-center gap-2 text-teal-700 text-[10.5px] font-bold uppercase tracking-[0.14em]">
              <CalendarRange size={14} strokeWidth={2.4} />
              Section 2 · Contract stack & renewals
            </div>
            <ul className="mt-3 space-y-3">
              {renewals.map((r) => (
                <li key={r.vendor}>
                  <div className="flex items-center justify-between text-[12px]">
                    <span className="text-navy font-bold truncate">{r.vendor}</span>
                    <span className="text-navy font-black">{r.value}</span>
                  </div>
                  <div className="mt-1.5 flex items-center gap-2">
                    <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className={`h-full rounded-full bg-gradient-to-r ${r.color}`}
                        style={{ width: `${Math.max(20, 100 - r.months * 8)}%` }}
                      />
                    </div>
                    <span className="text-body-muted text-[10.5px] font-bold whitespace-nowrap">{r.months}mo</span>
                  </div>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-body-muted text-[11px] leading-snug italic">
              Trigger windows: two contracts inside the next quarter.<sup className="text-teal-600 font-bold">¹</sup>
            </p>
          </div>
        </div>

        {/* Competitive set + Path preview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 lg:divide-x lg:divide-slate-100 border-t border-slate-100">
          <div className="px-6 sm:px-8 py-6">
            <div className="flex items-center gap-2 text-teal-700 text-[10.5px] font-bold uppercase tracking-[0.14em]">
              <Swords size={14} strokeWidth={2.4} />
              Section 3 · Competitive set
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {['Incumbent · Vendor A', 'Circling · Vendor B', 'Recently pitched · C', 'Gap: mid-market fit'].map((v, i) => (
                <div key={v} className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                  <p className="text-navy text-[11.5px] font-bold leading-tight">{v}</p>
                  <p className="text-body-muted text-[10.5px] mt-0.5">{i === 3 ? 'Defensible way in' : 'Public signal'}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="px-6 sm:px-8 py-6">
            <div className="flex items-center gap-2 text-teal-700 text-[10.5px] font-bold uppercase tracking-[0.14em]">
              <TrendingUp size={14} strokeWidth={2.4} />
              Section 4 · 12-week path
            </div>
            <ol className="mt-3 space-y-2 text-[11.5px]">
              {[
                'Wk 1–2 · Warm intro via publisher event',
                'Wk 3–5 · Insight brief + trigger email to CFO',
                'Wk 6–8 · Analyst-hosted webinar + follow-up',
                'Wk 9–12 · Proposal window · CFO/VP Mkt joint meet',
              ].map((step, i) => (
                <li key={step} className="flex items-start gap-2">
                  <span className="h-5 w-5 rounded-full bg-teal-600 text-white text-[9.5px] font-black flex items-center justify-center shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <span className="text-navy leading-snug">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* Footnotes */}
        <div className="px-6 sm:px-8 py-4 bg-brand-soft border-t border-slate-100">
          <p className="text-body-muted text-[10px] leading-snug">
            <sup className="text-teal-700 font-black">¹</sup> Contract disclosures & renewal cycles sourced from 10-K / 10-Q filings. &nbsp;
            <sup className="text-teal-700 font-black">²</sup> Q3 earnings call transcript. &nbsp;
            <sup className="text-teal-700 font-black">³</sup> Primary interview · executive on the record.
          </p>
        </div>
      </div>

      {/* Page indicator */}
      <div className="hidden md:flex absolute -right-3 top-6 flex-col items-center gap-1 rounded-full bg-white shadow-mk px-2 py-2 text-[10px] font-black text-body-muted">
        <span className="text-teal-700">1</span>
        <span>—</span>
        <span>13</span>
      </div>
    </div>
  );
};

export default AudienceIntelligenceMockup;
